import {
  AccountData,
  FollowerStatus,
  ImportData,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import { useCallback, useMemo, useState } from "react";
import filter from "lodash.filter";
import { Colors } from "../../../../../utils/colors";
import { MassTextsPersonItem } from "../../Items/MassTextsPersonItem";
import { EmptySearchState } from "../../../../EmptyStates/EmptySearchState";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { MassTextsActionItem } from "../../Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { SelectRecipientsSharedProps } from "../SelectRecipientsScreens";
import {
  addOrFilterRecipients,
  filterFollowersByStatus,
  textableContactString,
} from "@markit/common.utils";
import { getUserSpreadsheetContacts } from "../../../../../utils/spreadsheetUtils";
import { GetLongDate } from "../../../../../utils/GetLongDate";
import { useLoadSpreadsheetUserList } from "../../../../../hooks/useLoadSpreadsheetUserList";
import useAsyncOnMount from "../../../../../hooks/useAsyncEffectOnMount";
import VirtuosoList from "../../../../Lists/VirtuosoList";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";

type SelectRecipientsImportProps = SelectRecipientsSharedProps & {
  selectedImport: ImportData;
};

const SelectRecipientsImport = (props: SelectRecipientsImportProps) => {
  const {
    selectedImport,
    unsavedRecipients,
    setUnsavedRecipients,
    excludingMode,
    setProfileSelected,
    displayAllFollowers,
  } = props;
  const { accountData, followersData } = useSelector(getAccountState).account;
  const [peopleSearchTerm, setPeopleSearchTerm] = useState<string>("");
  const [allSpreadsheetUsers, setAllSpreadsheetUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    fetchedUserData: fetchedFollowerData,
    loadUsers: loadFollowerUsers,
    loadMoreUsers: loadMoreFollowerUsers,
    isLoading: isFetchedFollowersLoading,
    isFinished: isFinishedFollowers,
    loadSearchResultsThrottled,
  } = useLoadSpreadsheetUserList({
    userId: accountData.uid,
    followerStatus: displayAllFollowers ? undefined : FollowerStatus.SUBSCRIBED,
    windowSize: 20,
    fileId: selectedImport.spreadsheet.fid,
  });

  const allVisibleSpreadsheetUsers = useMemo(
    () =>
      displayAllFollowers
        ? allSpreadsheetUsers
        : filterFollowersByStatus(
            allSpreadsheetUsers,
            followersData,
            FollowerStatus.SUBSCRIBED
          ),
    [allSpreadsheetUsers, displayAllFollowers, followersData]
  );

  useAsyncOnMount(async () => {
    loadFollowerUsers();
    const spreadsheetUserIds = await getUserSpreadsheetContacts(
      accountData.uid,
      selectedImport.spreadsheet.fid
    );
    setAllSpreadsheetUsers(spreadsheetUserIds);
    setLoading(false);
  });

  const iconColor = useMemo(
    () => (excludingMode ? Colors.RED3 : Colors.BLUE5),
    [excludingMode]
  );

  const containsUser = useCallback((item: AccountData, query: string) => {
    return item.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    if (text !== "") {
      loadSearchResultsThrottled(text.toLowerCase());
    }
    setPeopleSearchTerm(text);
  };

  const itemsToShow: AccountData[] = useMemo(() => {
    if (peopleSearchTerm === "") {
      return fetchedFollowerData;
    }
    let userList: AccountData[] = fetchedFollowerData;
    userList = filter(userList, (user: AccountData) => {
      return containsUser(user, peopleSearchTerm.toLowerCase());
    });
    return userList;
  }, [peopleSearchTerm, fetchedFollowerData, containsUser]);

  const isAllSelected = useCallback(
    (totalList: string[], subList: string[]) =>
      subList.length > 0
        ? totalList.every((sel) => subList.includes(sel))
        : false,
    []
  );

  const renderHeader = useMemo(
    () => (
      <div>
        <MassTextsActionItem
          title={"Select All"}
          icon={
            <Icon icon="ion:people" height={32} style={{ color: iconColor }} />
          }
          onPress={() =>
            addOrFilterRecipients(
              unsavedRecipients,
              setUnsavedRecipients,
              allVisibleSpreadsheetUsers
            )
          }
          subtext={
            loading
              ? ""
              : `${allVisibleSpreadsheetUsers.length} ${
                  allVisibleSpreadsheetUsers.length !== 1 ? "People" : "Person"
                }`
          }
          isCheckSelected={isAllSelected(
            allVisibleSpreadsheetUsers,
            unsavedRecipients
          )}
          checkColor={iconColor}
        />
        <HorizontalDivider altMargin={10} />
      </div>
    ),
    [
      allVisibleSpreadsheetUsers,
      iconColor,
      isAllSelected,
      loading,
      setUnsavedRecipients,
      unsavedRecipients,
    ]
  );

  return (
    <>
      <div
        className="ColumnCenter"
        style={{
          gap: 4,
          backgroundColor: Colors.GRAY6,
          padding: 10,
          borderRadius: 12,
        }}
      >
        <span className="bodyMedium">
          {selectedImport.spreadsheet.fileName}
        </span>
        <span className="smallBodySubtext">
          {GetLongDate(selectedImport.spreadsheet.createdAt, true, true, true)}
        </span>
        <span className="smallBodySubtext">
          {textableContactString(selectedImport.textableContacts)}
        </span>
      </div>
      <SearchBoxContainer
        placeholder="Search People"
        onChange={(e) => handleSearch(e.target.value)}
        value={peopleSearchTerm}
        containerStyles={{ marginBottom: 10 }}
      />
      <VirtuosoList
        items={itemsToShow}
        loadMoreOnPress={loadMoreFollowerUsers}
        renderItem={(item) => {
          return (
            <MassTextsPersonItem
              user={item}
              unsavedPeople={unsavedRecipients}
              setUnsavedPeople={setUnsavedRecipients}
              excludingMode={excludingMode}
              setProfileSelected={setProfileSelected}
            />
          );
        }}
        isLoading={isFetchedFollowersLoading}
        isFinished={isFinishedFollowers}
        searchTerm={peopleSearchTerm}
        renderNonSearchEmptyView={
          <EmptySearchState mainText="No people to show" />
        }
        renderHeader={renderHeader}
      />
    </>
  );
};

export default SelectRecipientsImport;
